import { Info } from '@mui/icons-material';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

import { getAllFactoringExtensions } from '../../../../../lib/api/extension/factoring';
import CenteredCircularProgress from '../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../lib/designSystem/HeaderWithActions';
import { validatePermissions } from '../../../../../lib/functions/utils';
import { AuthState } from '../../../../../services/redux/authSlice';
import ExtensionDetails from './components/ExtensionDetails';
import MultiExtensionMain from './components/MultiExtension';

const Extensions: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [extensions, setExtensions] = useState<any>([]);
  const [openNewExtension, setOpenNewExtension] = useState(false);
  const [openMultiExtension, setOpenMultiExtension] = useState(false);
  const [edit, setEdit] = useState(false);
  const newExtension = {};
  const [extension, setExtension] = useState(newExtension);

  const [startDueDate, setStartDueDate] = useState(null);
  const [endDueDate, setEndDueDate] = useState(null);
  const [startCreatedAt, setStartCreatedAt] = useState(null);
  const [endCreatedAt, setEndCreatedAt] = useState(null);
  const [startActivationDate, setStartActivationDate] = useState(null);
  const [endActivationDate, setEndActivationDate] = useState(null);
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [stakeholderName, setStakeholderName] = useState('');
  const [folio, setFolio] = useState('');
  const [status, setStatus] = useState('');
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const handleShowBusinessDetails = (id: string) => {
    const row: any = extensions.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };

  const handleShowStakeholderDetails = (id: string) => {
    const row: any = extensions.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.stakeholderIdentifier,
        name: row.stakeholderName,
      }),
    );
  };

  const headers: { key: string; label: string; type?: any }[] = [
    { key: 'id', label: 'ID', type: 'numeric-id' },
    { key: 'folio', label: 'Folio documento' },
    { key: 'businessName', label: 'Cliente' },
    {
      key: 'businessIdentifier',
      label: 'Rut',
      ...(!user?.express && {
        handleAction: handleShowBusinessDetails,
      }),
      icon: <Info fontSize="small" />,
    },
    { key: 'stakeholderName', label: 'Deudor' },
    {
      key: 'stakeholderIdentifier',
      label: 'Rut',
      ...(!user?.express && {
        handleAction: handleShowStakeholderDetails,
        icon: <Info fontSize="small" />,
      }),
    },
    { key: 'status', label: 'Estado', type: 'status' },
    { key: 'documentAmount', label: 'Monto documento', type: 'money' },
    { key: 'operationCostFixed', label: 'Costo fijo', type: 'money' },
    { key: 'operationCostVariable', label: 'Costo variable', type: 'money' },
    { key: 'rateCost', label: 'Costo tasa', type: 'money' },
    { key: 'paymentDate', label: 'Vcto. original', type: 'date' },
    { key: 'dueDate', label: 'Vcto. prórroga', type: 'date' },
    { key: 'createdAt', label: 'Creado en', type: 'date-large' },
    { key: 'executives', label: 'Ejecutivo'},
  ];

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (startDueDate) query += `startDueDate=${startDueDate}&`;
    if (endDueDate) query += `endDueDate=${endDueDate}&`;
    if (startCreatedAt) query += `startCreatedAt=${startCreatedAt}&`;
    if (endCreatedAt) query += `endCreatedAt=${endCreatedAt}&`;
    if (startActivationDate)
      query += `startActivationDate=${startActivationDate}&`;
    if (endActivationDate) query += `endActivationDate=${endActivationDate}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (businessName)
      query += `businessName=${businessName.replace('&', '%26')}&`;
    if (stakeholderIdentifier)
      query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
    if (stakeholderName) query += `stakeholderName=${stakeholderName}&`;
    if (folio) query += `folio=${folio}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}&`;
    if (status) query += `status=${status}&`;
    return query;
  };

  const fetchExtensions = async ({
    page = 0,
    pageSize = PAGE_SIZE,
    exportComplete = false,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    try {
      setLoading(true);
      let query = generateQuery({
        page,
        pageSize,
      });
      if (exportComplete) {
        query += 'all=true&';
      }

      const response = await getAllFactoringExtensions(dispatch, query);
      setExtensions(response.data);
      setPagination(response.pagination);
      setLoading(false);

      return response;
    } catch (error) {
      alert('Error al cargar los pagos');
    }
    setLoading(false);
  };

  const handleNewMultiExtension = () => {
    setOpenMultiExtension(true);
  };

  const handleDetails = async (id: string) => {
    const extension = extensions.find((extension: any) => extension.id === id);
    if (!extension) return;
    setExtension(extension);
    setOpenNewExtension(true);
    setEdit(true);
  };

  const filters: Filter[] = [
    {
      label: 'Estado',
      type: 'select',
      value: status,
      setter: setStatus,
      options: [
        { label: 'Activa', value: 'ACTIVE' },
        { label: 'Completada', value: 'COMPLETED' },
        { label: 'Todas', value: 'ALL' },
      ],
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
    {
      label: 'Fecha de nuevo vencimiento',
      type: 'date',
      value: startDueDate,
      setter: setStartDueDate,
      dateKey: 'startDueDate',
      dateType: 'due',
      pairKey: 'endDueDate',
    },
    {
      label: 'Fecha de nuevo vencimiento',
      type: 'date',
      value: endDueDate,
      setter: setEndDueDate,
      dateKey: 'endDueDate',
      dateType: 'due',
      pairKey: 'startDueDate',
    },
    {
      label: 'Fecha de creación',
      type: 'date',
      value: startCreatedAt,
      setter: setStartCreatedAt,
      dateKey: 'startCreatedAt',
      dateType: 'created',
      pairKey: 'endCreatedAt',
    },
    {
      label: 'Fecha de creación',
      type: 'date',
      value: endCreatedAt,
      setter: setEndCreatedAt,
      dateKey: 'endCreatedAt',
      dateType: 'created',
      pairKey: 'startCreatedAt',
    },
    {
      label: 'Fecha de activación',
      type: 'date',
      value: startActivationDate,
      setter: setStartActivationDate,
      dateKey: 'startActivationDate',
      dateType: 'activation',
      pairKey: 'endActivationDate',
    },
    {
      label: 'Fecha de activación',
      type: 'date',
      value: endActivationDate,
      setter: setEndActivationDate,
      dateKey: 'endActivationDate',
      dateType: 'activation',
      pairKey: 'startActivationDate',
    },
    {
      label: 'Nombre cliente',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Rut emisor',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Nombre deudor',
      type: 'autocomplete',
      value: stakeholderName,
      key: 'stakeholderName',
      setter: setStakeholderName,
    },
    {
      label: 'Rut contraparte',
      type: 'text',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
  ];

  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Buscar',
      action: () => {},
      color: 'primary',
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={fetchExtensions}
          dispatch={dispatch}
          disableSearch={loading}
        />
      ),
    },
    {
      action: () => handleNewMultiExtension(),
      name: 'Nueva',
      color: 'primary',
      disabled: !validatePermissions(user, 'financing_extension_new'),
    },
  ];

  const triggerSearch = async () => {
    setLoading(true);
    await fetchExtensions({
      page: 0,
      pageSize: PAGE_SIZE,
    });
    setLoading(false);
  };

  useEffect(() => {
    triggerSearch();
  }, []);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        title="Acá encontrarás todas las prórrogas."
        variant="body1"
        buttons={buttonsData}
      />
      <TableWithPagination
        data={extensions}
        setData={setExtensions}
        dataHeaders={headers}
        pagination={pagination}
        setPagination={setPagination}
        searchData={fetchExtensions}
        loading={loading}
        setLoading={setLoading}
        exportToFile="xlsx"
        exportComplete={true}
        handleClickRow={handleDetails}
        lastElement
      />
      {openNewExtension && (
        <ExtensionDetails
          extension={extension}
          setExtension={setExtension}
          open={openNewExtension}
          setOpen={setOpenNewExtension}
          setUp={triggerSearch}
          edit={edit}
        />
      )}

      {openMultiExtension && (
        <MultiExtensionMain
          open={openMultiExtension}
          setOpen={setOpenMultiExtension}
          setUp={fetchExtensions}
          pagination={pagination}
        />
      )}
    </div>
  );
};

export default Extensions;
